/**
 * ChartData - Unified interface for chart data structures and utilities
 * Provides common data processing, validation, and transformation functions
 * used across all chart components.
 */

/**
 * Data item structure - can be a simple value or an object
 * @typedef {number|Object} ChartDataItem
 * @property {number} value - The numeric value (if object)
 * @property {string} [label] - Optional label for the item
 * @property {string} [color] - Optional color override
 * @property {string|number} [id] - Optional identifier for linking
 * @property {*} [custom] - Any additional custom properties
 */

/**
 * Chart data array structure
 * @typedef {ChartDataItem[]} ChartDataArray
 */

/**
 * Chart configuration interface
 * @typedef {Object} ChartConfig
 * @property {ChartDataArray} data - The chart data array
 * @property {string} [moduleId] - Optional module ID for fetching data from context
 * @property {number} [dataIndex] - Optional index for specific data selection
 * @property {string} [title] - Chart title
 * @property {string} [subtitle] - Chart subtitle
 * @property {boolean} [showLabels=true] - Whether to show labels
 * @property {boolean} [animate=true] - Whether to animate the chart
 * @property {string} [className=''] - Additional CSS classes
 * @property {*} [custom] - Any chart-specific custom properties
 */

/**
 * Normalizes a data item to a consistent object structure
 * @param {ChartDataItem} item - The data item to normalize
 * @param {number} [index] - Index for default label generation
 * @returns {Object} Normalized data item with value, label, and other properties
 */
export const normalizeDataItem = (item, index = 0) => {
  if (typeof item === 'object' && item !== null) {
    return {
      value: item.value ?? 0,
      label: item.label ?? `Item ${index + 1}`,
      color: item.color,
      id: item.id,
      ...item
    };
  }
  return {
    value: item,
    label: `Item ${index + 1}`
  };
};

/**
 * Normalizes an entire data array to consistent object structure
 * @param {ChartDataArray} data - The data array to normalize
 * @returns {Object[]} Normalized data array
 */
export const normalizeDataArray = (data) => {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((item, index) => normalizeDataItem(item, index));
};

/**
 * Extracts the numeric value from a data item
 * @param {ChartDataItem} item - The data item
 * @returns {number} The numeric value
 */
export const getItemValue = (item) => {
  if (typeof item === 'object' && item !== null) {
    return item.value ?? 0;
  }
  return item;
};

/**
 * Extracts the label from a data item
 * @param {ChartDataItem} item - The data item
 * @param {number} [index] - Index for default label generation
 * @returns {string} The label
 */
export const getItemLabel = (item, index = 0) => {
  if (typeof item === 'object' && item !== null) {
    return item.label ?? `Item ${index + 1}`;
  }
  return `Item ${index + 1}`;
};

/**
 * Extracts the color from a data item
 * @param {ChartDataItem} item - The data item
 * @param {string} [defaultColor] - Default color if none specified
 * @returns {string|undefined} The color or undefined
 */
export const getItemColor = (item, defaultColor) => {
  if (typeof item === 'object' && item !== null) {
    return item.color ?? defaultColor;
  }
  return defaultColor;
};

/**
 * Extracts the ID from a data item
 * @param {ChartDataItem} item - The data item
 * @param {string} [key='id'] - The key to use for ID
 * @returns {string|number|undefined} The ID or undefined
 */
export const getItemId = (item, key = 'id') => {
  if (typeof item === 'object' && item !== null) {
    return item.id ?? item[key];
  }
  return undefined;
};

/**
 * Calculates the maximum value from a data array
 * @param {ChartDataArray} data - The data array
 * @returns {number} The maximum value
 */
export const getMaxValue = (data) => {
  if (!Array.isArray(data) || data.length === 0) {
    return 0;
  }
  return Math.max(...data.map(item => getItemValue(item)));
};

/**
 * Calculates the minimum value from a data array
 * @param {ChartDataArray} data - The data array
 * @returns {number} The minimum value
 */
export const getMinValue = (data) => {
  if (!Array.isArray(data) || data.length === 0) {
    return 0;
  }
  return Math.min(...data.map(item => getItemValue(item)));
};

/**
 * Calculates the sum of values in a data array
 * @param {ChartDataArray} data - The data array
 * @returns {number} The sum of values
 */
export const getSumValue = (data) => {
  if (!Array.isArray(data) || data.length === 0) {
    return 0;
  }
  return data.reduce((sum, item) => sum + getItemValue(item), 0);
};

/**
 * Calculates the average value from a data array
 * @param {ChartDataArray} data - The data array
 * @returns {number} The average value
 */
export const getAverageValue = (data) => {
  if (!Array.isArray(data) || data.length === 0) {
    return 0;
  }
  return getSumValue(data) / data.length;
};

/**
 * Validates if data is a valid chart data array
 * @param {*} data - The data to validate
 * @returns {boolean} True if valid chart data
 */
export const isValidChartData = (data) => {
  return Array.isArray(data) && data.length > 0;
};

/**
 * Gets chart data from either direct data prop or module context
 * @param {ChartDataArray} data - Direct data prop
 * @param {*} moduleData - Data from module context
 * @param {ChartDataArray} [defaultData] - Default data if both are invalid
 * @returns {ChartDataArray} The resolved chart data
 */
export const resolveChartData = (data, moduleData, defaultData = [65, 78, 92, 45, 67]) => {
  if (Array.isArray(moduleData) && moduleData.length > 0) {
    return moduleData;
  }
  if (Array.isArray(data) && data.length > 0) {
    return data;
  }
  return defaultData;
};

/**
 * Sorts data array by value in ascending or descending order
 * @param {ChartDataArray} data - The data array to sort
 * @param {string} [order='desc'] - Sort order: 'asc' or 'desc'
 * @returns {ChartDataArray} Sorted data array
 */
export const sortDataByValue = (data, order = 'desc') => {
  if (!Array.isArray(data)) {
    return [];
  }
  return [...data].sort((a, b) => {
    const valueA = getItemValue(a);
    const valueB = getItemValue(b);
    return order === 'asc' ? valueA - valueB : valueB - valueA;
  });
};

/**
 * Filters data array based on a predicate function
 * @param {ChartDataArray} data - The data array to filter
 * @param {Function} predicate - Filter function
 * @returns {ChartDataArray} Filtered data array
 */
export const filterData = (data, predicate) => {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.filter(predicate);
};

/**
 * Maps over data array with normalized items
 * @param {ChartDataArray} data - The data array to map
 * @param {Function} mapper - Map function that receives normalized item
 * @returns {Array} Mapped array
 */
export const mapData = (data, mapper) => {
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((item, index) => mapper(normalizeDataItem(item, index), index));
};

/**
 * Generates default chart data for testing/placeholder
 * @param {number} [count=5] - Number of data points to generate
 * @param {number} [min=0] - Minimum value
 * @param {number} [max=100] - Maximum value
 * @returns {ChartDataArray} Generated default data
 */
export const generateDefaultData = (count = 5, min = 0, max = 100) => {
  return Array.from({ length: count }, (_, i) => ({
    value: Math.floor(Math.random() * (max - min + 1)) + min,
    label: `Item ${i + 1}`
  }));
};

/**
 * Chart data utilities object for convenient importing
 */
export const ChartDataUtils = {
  normalizeDataItem,
  normalizeDataArray,
  getItemValue,
  getItemLabel,
  getItemColor,
  getItemId,
  getMaxValue,
  getMinValue,
  getSumValue,
  getAverageValue,
  isValidChartData,
  resolveChartData,
  sortDataByValue,
  filterData,
  mapData,
  generateDefaultData
};

export default ChartDataUtils;
